import { LoadingOutlined } from '@ant-design/icons';
import { Dropdown, Menu, Spin } from 'antd';
import { isDefined } from 'class-validator';
import { Feature, FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import L, { LatLngBoundsExpression, LatLngExpression } from 'leaflet';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import SwitchLayerIcon from 'public/SwitchLayerIcon.png';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CountriesController } from 'src/app/components/LeafletMapComponent/controllers/CountriesController';
import { DrawController } from 'src/app/components/LeafletMapComponent/controllers/DrawController';
import { LayersController } from 'src/app/components/LeafletMapComponent/controllers/LayersController';
import { MapMarkerController } from 'src/app/components/LeafletMapComponent/controllers/MapMarkerController';
import InterestMarkerInfoBox from 'src/app/components/LeafletMapComponent/InterestMarkerInfoBox/InterestMarkerInfoBox';
import {
  CustomMarker,
  customMarkers,
  ILeafletMapProps,
  InterestLocation,
  markerGenerator,
  OverLappedInterests,
} from 'src/app/components/LeafletMapComponent/leaflet.config';
import OverlappedInterests from 'src/app/components/LeafletMapComponent/overLappedInterests/OverlappedInterests';
import { getRandomColor } from 'src/app/helpers/coordinates.helper';
import useBulkInterestList from 'src/app/hooks/useBulkInterestList';
import { useAppDispatch, useAppSelector } from 'src/app/redux/hook';
import { MapInstance } from 'src/app/redux/map/map.redux.type';
import { getLayersRedux, setActiveLayer } from 'src/app/redux/map/mapSlice';
import { axios } from 'src/app/utils';
import { finalUrlsConfig } from 'src/configs/url.config';
import { IdValuePairType } from 'src/types/interests.type';

// @ts-ignore
window.type = true;

const LeafletMap: React.FC<ILeafletMapProps> = React.memo(
  ({
    mapOptions,
    drawOptions,
    events,
    lastCoordinates,
    interestIdentifiedLocations,
    mapId,
    style,
    generalMapViewProps,
    onMapRendered,
    isDashboard,
  }) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const [map, setMap] = useState<L.Map | undefined>(undefined);
    const [, setDrawController] = useState<DrawController>();
    const [mapMarkerController, setMapMarkerController] = useState<MapMarkerController | undefined>();
    const [overLappedMarkers, setOverLappedMarkers] = useState<OverLappedInterests | undefined>();
    const [selectedInterest, setSelectedInterest] = useState<CustomMarker | undefined>();
    const [coordinates, setCoordinates] = useState<{ lat: number; lng: number }>({
      lat: 0.0,
      lng: 0.0,
    });
    const [interestsForNetworkSearch, setInterestsForNetworkSearch] = useState<IdValuePairType[]>([]);
    // const { data: interestList } = useGetAllInterestsQuery({
    //   customerStatus: true,
    // });

    const interestIds = useMemo(() => {
      return interestIdentifiedLocations?.map((identifiedLocation) => identifiedLocation?.id) || [];
    }, [interestIdentifiedLocations]);

    const interestList = useBulkInterestList({ ids: interestIds, customerStatus: true });

    // Layers controller is added for NSearch Map View and coordinates tabs,
    // User can add and delete his/her own map layers and see the results on these tabs.
    const [layersController, setLayersController] = useState<LayersController | undefined>(undefined);

    // Countries controller is added for Location, Enterprise identified Map View tabs,.
    const [countriesController, setCountriesController] = useState<CountriesController | undefined>(undefined);

    // As it explained above, activeLayer, avaiLable layers are both used for NSearch tabs.
    // mapInstance is a globalVariable, it helps client to recognize which map is rendering atm.
    const { activeLayer, availableLayers, mapInstance } = useAppSelector((state) => state.map);

    // countriesFeatureCollection, is a leaflet object that contains all of the countries, their event listeners etc,
    // We are using this feature collection inside the Countries Controller
    const [countriesFeatureCollection, setCountriesFeatureCollection] = useState<FeatureCollection | undefined>();

    // const { onInterestClick } = useInterestTableClick();

    useEffect(() => {
      map?.invalidateSize();
    }, [style.width, style.height]);

    useEffect(() => {
      if (!map && mapRef.current) {
        // as soon as the component is loaded we add a map.
        const mapObject = L.map(mapId, mapOptions);
        setMap(mapObject);
      }

      // If it is an NSearchMap we get layers.
      if (mapId !== MapInstance.COUNTRIES) {
        dispatch(getLayersRedux());
      }
    }, [map, countriesFeatureCollection]);

    useEffect(() => {
      const getFeatureCollection = async () => {
        try {
          await axios
            .get(`${finalUrlsConfig().webServerBaseURL}/${finalUrlsConfig().geoJSONCountries.getCountries}`, {
              blockSpinner: true,
            })
            .then((res) => {
              const features = res.data.map((feature: any) => {
                return feature.geoJSON;
              });
              const featureCollection: FeatureCollection = {
                type: 'FeatureCollection',
                features: features as Feature<Geometry, GeoJsonProperties>[],
              };
              setCountriesFeatureCollection(featureCollection);
            });
          // .catch((error) => {
          //   console.log('err', error);
          // });
        } catch (error) {
          console.log('error', error);
        }
      };

      // If it's not a NSearch map, then we get featureCollection, which comes from the backend.
      if (generalMapViewProps) {
        getFeatureCollection();
      }
    }, []);

    useEffect(() => {
      if (map !== undefined) {
        setDrawController(new DrawController(map, drawOptions, events));
        // if it is a NSearch map, we create a local instance for the MapMarkerController class, which
        // helps us to draw the map markers such as interest locations, polylines etc...
        if (mapId === MapInstance.DEFAULT) {
          const localMarkerController = MapMarkerController.getInstance(map);
          localMarkerController.onConstruct({
            map,
            setOverLappedMarkers,
            setSelectedInterest,
          });
          setMapMarkerController(localMarkerController);
        }
        /*
          If it is not a NSearch map, we do not want to keep track of the coordinates for the mouse hovering over.
        */
        if (mapId !== MapInstance.COUNTRIES) {
          map.on('mousemove', (e: L.LeafletMouseEvent) => {
            const { lat, lng } = e.latlng.wrap();
            setCoordinates({ lat, lng });
          });
          const localLayersController = LayersController.getInstance();
          setLayersController(localLayersController);
        }
      }
    }, [map, mapId]);

    // LayersController
    useEffect(() => {
      if (
        layersController &&
        availableLayers.length &&
        map &&
        mapInstance === mapId &&
        mapId !== MapInstance.COUNTRIES
      ) {
        layersController.setLayersSource(availableLayers, map, mapId);
      }
    }, [layersController, availableLayers, mapInstance]);

    // IMPORTANT!!!!! If you have some trouble with the markers, you need to add mapInstance === mapId condition too.
    useEffect(() => {
      if (map && layersController && activeLayer && mapId !== MapInstance.COUNTRIES) {
        // We use this function change current maps layer for both NSearch maps,
        // User can add his/her own layers on Map Settings tab.
        layersController.handleLayerChange(activeLayer, map);
      }
    }, [activeLayer, map, layersController]);

    // IMPORTANT!!!!! If you have some trouble with the markers, you need to add mapInstance === mapId condition too.
    useEffect(() => {
      if (!activeLayer && availableLayers.length && mapId !== MapInstance.COUNTRIES) {
        const defaultActiveLayerId = availableLayers.find((layer) => layer.isActive && layer.id !== undefined)?.id;
        if (defaultActiveLayerId) {
          dispatch(setActiveLayer(defaultActiveLayerId));
        }
      }
    }, [activeLayer, availableLayers, mapInstance]);

    // LastLocations
    useEffect(() => {
      if (map !== undefined) {
        if (isDefined(lastCoordinates) && lastCoordinates?.length > 0) {
          const bounds = lastCoordinates.map((coordinate) => [coordinate.latitude, coordinate.longitude]);

          if (lastCoordinates.length === 1) {
            L.marker(bounds[0] as LatLngExpression, {
              icon: customMarkers.lastLocation('last-loc'),
            }).addTo(map);
          } else {
            L.rectangle(bounds as LatLngBoundsExpression, { color: '#ff7800', weight: 1 }).addTo(map);
          }
        }
      }
    }, [lastCoordinates, map]);

    // InterestIdentified
    /*
    The use effect below is 100% about MapView tab on NSearch page.
    */
    useEffect(() => {
      if (map !== undefined && mapMarkerController !== undefined) {
        mapMarkerController.resetMapMarkerController();

        const getInterestAndMarkers = () => {
          mapMarkerController.deletePrevMarkersOfFilterInterest();
          // Firstly we get interestIdentifiedLocations as prop, which contains interests coordinates, id, and type information.
          if (isDefined(interestIdentifiedLocations) && interestIdentifiedLocations?.length > 0) {
            /* 
              Below we will create an array for the markers of these interestIdentifiedLocations, we call this array 
              as "primeMarkers" array.
            */
            const primeMarkers: CustomMarker[] = [];
            const newInterestLocationObj: Record<string, InterestLocation> = {};
            /* 
              We map over the interestIdentifiedLocations and create markers and other necessary informations for the
              MapMarkerController
            */
            interestIdentifiedLocations.forEach((interestLocation) => {
              const newMarkerToAdd = markerGenerator(
                [interestLocation.lat, interestLocation.lng],
                interestLocation.label || '',
                map,
                customMarkers.lastLocation(interestLocation.id),
                interestLocation.id,
              );
              primeMarkers.push({
                id: interestLocation.id,
                marker: newMarkerToAdd,
                label: interestLocation.label,
                coordinates: [interestLocation.lat, interestLocation.lng],
                time: interestLocation.time,
                blockID: interestLocation.blockID,
                prime: true,
              });
              newInterestLocationObj[interestLocation.id] = {
                id: interestLocation.id,
                coordinates: [interestLocation.lat, interestLocation.lng] as [number, number],
                viewSubs: false,
                label: interestLocation.label,
                marker: newMarkerToAdd,
                time: interestLocation.time,
                blockID: interestLocation.blockID,
                color: getRandomColor(),
              };
            });
            /* 
              After we have all the markers, we call the MapMarkerController to generate functionality and
              add these markers to the map.
            */
            mapMarkerController.getPrimeMarkerFeatureGroup(primeMarkers);
            mapMarkerController.generateFunctionality({
              interestLocations: newInterestLocationObj,
            });
          }
        };
        getInterestAndMarkers();
      }
    }, [map, interestIdentifiedLocations, mapMarkerController, mapId]);

    useEffect(() => {
      let localCountriesController: CountriesController;

      /*
        again if mapInstance is Countries, we initialize countries controller locally
      */
      if (map !== undefined && mapInstance === MapInstance.COUNTRIES) {
        localCountriesController = CountriesController.getInstance();
        localCountriesController.setCountryColor(isDashboard ? '#CFDBD5' : '');
        localCountriesController.setBorderColor(isDashboard ? '#fff' : '');
        localCountriesController.setIsDashboard(isDashboard ?? false);
        setCountriesController(localCountriesController);
      }

      return () => {
        if (localCountriesController) {
          localCountriesController.resetCountriesControllers();
          map?.clearAllEventListeners();
          map?.remove();
        }
      };
    }, [map, mapInstance]);

    useEffect(() => {
      if (isDefined(interestIdentifiedLocations) && interestIdentifiedLocations?.length > 0) {
        const newInterestsForNetworkSearch: any = [];
        interestList?.rows?.forEach((int) =>
          int?.interests?.forEach((interest) => {
            newInterestsForNetworkSearch?.push(interest);
          }),
        );
        setInterestsForNetworkSearch(newInterestsForNetworkSearch);
      }
    }, [interestIdentifiedLocations, interestList]);
    useEffect(() => {
      if (
        mapInstance === MapInstance.COUNTRIES &&
        countriesController &&
        map !== undefined &&
        generalMapViewProps &&
        countriesFeatureCollection
      ) {
        // This use effect keeps track of the changes that comes from above Components,
        // and updates colors, events etc.
        countriesController.addLayersForCountryPages({ map, generalMapViewProps, countriesFeatureCollection });
        if (onMapRendered) {
          onMapRendered();
        }
      }
    }, [map, mapInstance, countriesController, generalMapViewProps, countriesFeatureCollection]);

    const layerMenuOnClick = (id: string) => {
      dispatch(setActiveLayer(id));
    };

    const toggleLayerMenu = (
      <Menu onClick={(e) => layerMenuOnClick(e.key)}>
        {availableLayers
          .filter((o) => o.id && o.isBaseLayer && o.isActive)
          .map((layer) => (
            <Menu.Item key={layer.id}>{layer.label}</Menu.Item>
          ))}
      </Menu>
    );

    if (generalMapViewProps && !countriesFeatureCollection)
      return (
        <div style={{ display: 'flex', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        </div>
      );

    return (
      <>
        {overLappedMarkers && overLappedMarkers?.overlappedArray?.length >= 2 && (
          <OverlappedInterests overLappedInterests={overLappedMarkers} mapMarkerController={mapMarkerController} />
        )}
        {selectedInterest && (
          <InterestMarkerInfoBox mapMarkerController={mapMarkerController} selectedInterest={selectedInterest} />
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          {mapId !== MapInstance.COUNTRIES && (
            <Dropdown overlay={toggleLayerMenu}>
              <img
                src={SwitchLayerIcon}
                alt="Switch Layer"
                style={{
                  width: '50px',
                  height: '50px',
                  position: 'absolute',
                  zIndex: 1,
                  right: '0.5em',
                  bottom: '0.5em',
                  cursor: 'pointer',
                }}
              />
            </Dropdown>
          )}
          {/* {mapId !== MapInstance.COUNTRIES && (
            <Select
              style={{
                position: 'absolute',
                zIndex: 1,
                top: '2.20em',
                left: '4em',
                cursor: 'pointer',
              }}
              defaultValue="Network Analysis of Interest"
              options={interestsForNetworkSearch?.map((eachInterest) => ({
                label: eachInterest.value,
                value: eachInterest.id,
              }))}
              onChange={(id) => {
                const interestToSearch = interestsForNetworkSearch.find((interest) => interest.id === id);
                if (interestToSearch) {
                  onInterestClick(
                    {
                      ...interestToSearch,
                      name: `${interestToSearch.value}`,
                      classType: interestToSearch.classType,
                      type: interestToSearch.type,
                    },
                    InterestComponents.Network,
                  );
                }
              }}
            >
              Network Analysis
            </Select> 
          )} */}
          {mapInstance !== MapInstance.COUNTRIES && (
            <p style={{ margin: '0 auto 0.25em 0' }}>
              {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
            </p>
          )}
          <div ref={mapRef} id={mapId} className="leaflet-map" style={style} />
        </div>
      </>
    );
  },
);

LeafletMap.defaultProps = {
  mapOptions: {
    center: [38.423733, 27.142826],
    zoom: 1,
    zoomSnap: 0.25,
    minZoom: 2,
    maxZoom: 7,
    maxBounds: [
      [-85, -180],
      [85, 180],
    ],
    maxBoundsViscosity: 1.0,
    attributionControl: false,
  },
};

LeafletMap.displayName = 'LeafletMap';
export default LeafletMap;
